import type {Notification} from 'contexts/NotificationContext';
import {genericMessageHandler} from 'shared/helpers';

type NotifyFn = (notifications: Notification[], duration?: number) => void;

let notifyRef: NotifyFn | null = null;

/**
 * Registers `NotificationContext.notify` from `RoomPlannerProvider` so non-React helpers
 * (e.g. `renderCabinetGroup`) can use the same toasts as `AddCabinetPopup` via
 * `genericMessageHandler`.
 *
 * @param {NotifyFn | undefined} notify From `useNotificationContext().notify`.
 */
export const registerRoomPlannerNotify = (notify: NotifyFn | undefined) => {
    notifyRef = notify ?? null;
};

export const unregisterRoomPlannerNotify = () => {
    notifyRef = null;
};

/** Matches `Notification.variant` / `genericMessageHandler` `type`. */
export type RoomPlannerNotifyVariant =
    | 'danger'
    | 'success'
    | 'info'
    | 'warning';

export type RoomPlannerNotifyOptions = {
    message: string;
    variant?: RoomPlannerNotifyVariant;
};

/** User-facing copy when duplicate placement cannot find a free spot. */
export const ROOM_PLANNER_MSG_DUPLICATE_NO_SPACE =
    'There is not enough free space in the room to place a duplicate of this cabinet.';

/**
 * Show a room-planner toast using the same path as `AddCabinetPopup` when `notify` is registered.
 *
 * @param {RoomPlannerNotifyOptions | string} optionsOrMessage Options object, or shorthand message string (uses `danger`, 5000ms).
 * @return {void}
 */
export const notifyFromRoomPlanner = (
    optionsOrMessage: RoomPlannerNotifyOptions
): void => {
    const {message, variant} = optionsOrMessage;

    genericMessageHandler(notifyRef, {message}, variant);
};

/**
 * Preset copy for duplicate placement when no free spot was found.
 *
 * @return {void}
 */
export const notifyNoSpaceForDuplicate = (): void =>
    notifyFromRoomPlanner({
        message: ROOM_PLANNER_MSG_DUPLICATE_NO_SPACE,
    });
